import classNames from "classnames";
import type { FC } from "react";
import { useLayoutEffect, useRef } from "react";
import { v4 as uuid } from "uuid";
import { Label } from "../../../components/label/label";
import { useFactory } from "../../../helpers/reactHooks";
import styles from "./pp2PayForm.module.scss";

export type Pp2ReadOnlyTextProps = {
  label: string;
  value: string;
  className?: string;
};

/**
 * A preset text field (spec 5.2): looks like the local `Input` but is a read-only `<textarea>` as
 * tall as its text, so a long preset description wraps instead of being cut off at the field's edge.
 * Its height follows the width it wraps at, and the web font once it has loaded.
 */
export const Pp2ReadOnlyText: FC<Pp2ReadOnlyTextProps> = ({ label, value, className }) => {
  const id = useFactory(() => uuid());
  const ref = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    const textarea = ref.current;
    if (!textarea) {
      return undefined;
    }
    let width = -1;
    const fit = (): void => {
      width = textarea.clientWidth;
      textarea.style.height = "auto";
      // border-box: the scroll height leaves out the 1px top and bottom borders
      textarea.style.height = `${textarea.scrollHeight + textarea.offsetHeight - textarea.clientHeight}px`;
    };
    const fitNewWidth = (): void => {
      // Only a new width changes the wrapping here; the height set by `fit` must not refit
      if (textarea.clientWidth !== width) {
        fit();
      }
    };
    fit();
    const observer = new ResizeObserver(fitNewWidth);
    observer.observe(textarea);
    // The first fit may measure a fallback font: once the web font arrives the text can wrap onto
    // more or fewer lines at the same width, so fit again whenever fonts finish loading
    const fonts = "fonts" in document ? document.fonts : null;
    let active = true;
    const fitAfterFonts = (): void => {
      if (active) {
        fit();
      }
    };
    fonts?.addEventListener("loadingdone", fitAfterFonts);
    void fonts?.ready.then(fitAfterFonts);
    return () => {
      active = false;
      observer.disconnect();
      fonts?.removeEventListener("loadingdone", fitAfterFonts);
    };
  }, [value]);

  return (
    <span className={classNames(styles.readOnlyText, className)}>
      <Label forId={id} labelText={label} />
      <textarea ref={ref} id={id} className={styles.readOnlyTextArea} value={value} rows={1} readOnly />
    </span>
  );
};
